import type { z } from 'zod'

import type { OfferFetchBuyXGetYSchema } from '@/ducks/offers/offers.schema'

import type { BuyXGetYCampaign } from './campaigns.types'

export type BuyXGetYOffer = z.infer<typeof OfferFetchBuyXGetYSchema>[number]

type CampaignItemRef = { _id?: string; name?: string }

export const findBuyXGetYOfferByCampaign = (
  offers: BuyXGetYOffer[] | undefined,
  campaign: BuyXGetYCampaign
): BuyXGetYOffer | null => {
  if (!offers?.length) return null

  const byOfferId = offers.find(offer => offer._id === campaign.campaignId)
  if (byOfferId) return byOfferId

  const campaignItems = campaign.items as CampaignItemRef[] | undefined
  const buyItemId = campaign.itemToBuy?.id
  const buyItemName = campaign.itemToBuy?.name ?? campaign.firstItem
  const getItemId = campaign.itemToGet?.id ?? campaignItems?.[0]?._id
  const getItemName = campaign.itemToGet?.name ?? campaignItems?.[0]?.name

  return (
    offers.find(offer => {
      const item1Match =
        (!buyItemId || offer.item1._id === buyItemId) &&
        (!buyItemName || offer.item1.name === buyItemName)
      const item2Match =
        (!getItemId && !getItemName) ||
        offer.item2.some(
          item =>
            (getItemId != null && item._id === getItemId) ||
            (getItemName != null && item.name === getItemName)
        )
      const countsMatch =
        (campaign.nbrItemsPurchased == null ||
          offer.nbr_items_purchased === campaign.nbrItemsPurchased) &&
        (campaign.nbrItemsOffer == null ||
          offer.nbr_items_offer === campaign.nbrItemsOffer)
      const expiryMatch =
        campaign.expiryDate == null || offer.expiry_date === campaign.expiryDate

      return item1Match && item2Match && countsMatch && expiryMatch
    }) ?? null
  )
}

export const getBuyXGetYOfferId = (
  campaign: BuyXGetYCampaign,
  offers: BuyXGetYOffer[] | undefined
): string | null =>
  findBuyXGetYOfferByCampaign(offers, campaign)?._id ?? null

/** Expired offers may be absent from GET /buyXGetYOffer; campaigns still expose the offer id as campaignId. */
export const getBuyXGetYDeleteOfferId = (
  campaign: BuyXGetYCampaign,
  offers: BuyXGetYOffer[] | undefined
): string | null =>
  getBuyXGetYOfferId(campaign, offers) ??
  (campaign.campaignId?.trim() || null)

export const isBuyXGetYOfferNotFoundError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false

  const { message } = error as { message?: string }

  return typeof message === 'string' && /offer not found/i.test(message)
}

export const mapBuyXGetYOfferToEditState = (offer: BuyXGetYOffer) => ({
  offerId: offer._id,
  expiryDate: offer.expiry_date,
  expirationTime: offer.expiration_time,
  itemsPurchased: offer.nbr_items_purchased,
  itemsOffer: offer.nbr_items_offer,
  isCumulative: offer.cumulatif,
  item1: {
    id: offer.item1._id,
    name: offer.item1.name
  },
  item2: offer.item2[0]
    ? {
        id: offer.item2[0]._id,
        name: offer.item2[0].name,
        discountPercent: offer.item2[0].discount_percent
      }
    : null,
  status: offer.status
})
